<template>
  <div class="cmp-contact-form-user-old">
    <ws-input
      ref="login"
      v-model="login"
      name="username"
      :label="$t('scf.user_old.login.label')"
      autocomplete="username"
      :path="path"
      debounce="300"
      required
      :lazy-rules="false"
      :restrict="loginValidation.restriction"
      :rules="[required, ...loginValidation.rules]"
      @input="checkLoginExistence"
    >
      <template v-slot:append>
        <span v-if="loading" class="loading-msg">
          <q-spinner class="q-mr-sm float-left" />
          {{ $t('scf.user_old.login.loading_msg') }}
        </span>
        <span
          v-if="!loading && login && login === model.contact.login"
          class="done-msg"
        >
          <ws-icon
            name="check"
            class="q-mr-sm"
            color="ws-primary"
          />{{ $t('scf.user_old.login.done_msg') }}</span>
      </template>
    </ws-input>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { QSpinner } from 'quasar';
import {
  validateInput,
  required,
  propertyValidation,
  generalPropertyList,
  addValidations,
} from '@/services/validation/validation.service.ts';
import type { PropertyValidation } from '@/services/validation/validation.service.d';
import WsInput from '@WS_Components/WsInput.vue';
import { WsIcon } from '@loopia-group/components-vue2';
import { checkLogin } from '@/services/user-api.service';
import type { LoginCheckResponse } from '@/types';

@Component({
  components: {
    WsIcon,
    QSpinner,
    WsInput,
  },
})
export default class ContactFormUserOld extends Vue {
  @Prop({ default: () => ({ contact: {} }) })
  readonly value!: any;

  loading = false;
  login = '';
  path = 'scf.contact.login';
  loginValidation!: PropertyValidation;

  data() {
    return {
      required,
    };
  }

  get model() {
    return { ...this.value };
  }

  @Watch('loading')
  loadingWatch(loading: boolean) {
    this.$emit('loading', loading);
  }

  @Watch('model.contact.login', { immediate: true })
  loginWatch(login: string) {
    this.login = login || '';
  }

  created() {
    addValidations([generalPropertyList]);
    this.loginValidation = propertyValidation('login');
  }

  async checkLoginExistence(login: string) {
    await this.$nextTick(); // wait for qField to update internal values so validation is evaluated correctly
    const result = await validateInput(this.$refs.login);
    if (!result) {
      return;
    }

    this.loading = true;

    try {
      const data: LoginCheckResponse = await checkLogin(login);
      if (login !== this.login) {
        return; // ignore if it is not latest value
      }

      if (data.free) {
        this.$messageService.errorHandler(this.path)(
          'validation.login.missing'
        );
      } else {
        this.model.contact.login = login;
        this.$messageService.clearMessages(this.path);
        this.reEmit();
      }
    } catch (err) {
      if (login !== this.login) {
        return; // ignore if it is not latest value
      }
      this.$messageService.errorHandler(this.path)(err);
    }

    this.loading = false;
  }

  reEmit() {
    this.$emit('input', this.model);
  }
}
</script>

<style lang="scss" scoped>
.loading-msg,
.done-msg {
  font-size: 14px;
  font-weight: bold;
  margin-right: map-get($space-lg, x);
  @media (max-width: $ws-breakpoint-mobile-max) {
    margin-right: 0;
  }
}
.loading-msg {
  color: $ws-base150;
}
.done-msg {
  color: $ws-green;
}
</style>
